import { Navigate, useLocation } from "react-router-dom";
import { useUserAuth } from "../context/UserContext";
import { isPrivilegedRole } from "../utils/roles";
import { COURSES_OPEN } from "../config/courses";

// Every path that shows course material. Listed here rather than as a wrapper
// around ~110 <Route> elements in App.js so there is one place to change, and
// so lessons added later are covered by their prefix without another edit.
const COURSE_PATHS = [
    "/courses",
    "/javascript-table",
    "/claudedev-table",
    "/claudedev-v2-table",
    "/claudedev-v3-table",
    "/claudedev-v4-table",

    // Legacy Java/JS scaffolding pages. Nothing in the user-facing nav links to
    // them, but they are typeable URLs that still render half-built lesson
    // material, so they close too. (/courses2, /lesson, /quiz and the two Java
    // test pages are not listed: they are staff-only for good, see
    // router/StaffOnlyRoute.js, which sends everyone else to the homepage.)
    "/basicstructure",
    "/syntax",
    "/printstatements",
    "/comments",
    "/indentation",
    "/indentationinjava",
];

// Lesson routes: JS01, the Claude course, and CD01–CD04. Every lesson id in the
// registries starts with one of these.
const COURSE_PREFIXES = ["/js_m", "/cc_m", "/cd_m", "/v2_m", "/v3_m", "/v4_m"];

// Matched case-insensitively and without a trailing slash because React Router
// matches paths that way too — /v4_m1_l1 and /courses/ reach the same routes as
// /V4_M1_L1 and /courses, so every spelling has to be caught.
export function isCoursePath(pathname = "") {
    const path = String(pathname).toLowerCase().replace(/\/+$/, "") || "/";
    return (
        COURSE_PATHS.includes(path) ||
        COURSE_PREFIXES.some((prefix) => path.startsWith(prefix))
    );
}

/**
 * Blocks the whole course area while COURSES_OPEN is false.
 *
 * Wraps <Routes> instead of sitting beside it: a sibling that returns <Navigate>
 * would let the course page mount and paint for a frame (and fire its content
 * fetch) before the redirect landed. Returning the redirect *instead of* the
 * children means the course page never renders at all.
 *
 * Safe to read the role here — UserAuthContextProvider renders `{!loading &&
 * children}`, so auth has already resolved by the time this runs and an admin
 * refreshing on a lesson page is not bounced before their role arrives.
 */
export function CoursesAreaGate({ children }) {
    const { user } = useUserAuth();
    const location = useLocation();

    if (COURSES_OPEN || isPrivilegedRole(user?.role)) return children;
    if (!isCoursePath(location.pathname)) return children;

    // /subscriptions already shows the "Enrollment isn't open yet" notice while
    // ENROLLMENT_OPEN is false, so the two closures read as one message.
    return <Navigate to="/subscriptions" replace />;
}

export default CoursesAreaGate;
